import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { useCare } from '../../contexts/CareContext'
import { useToast, humanizeError } from '../shared/Toast'
import LoadingSpinner from '../shared/LoadingSpinner'
import EmptyState from '../shared/EmptyState'
import { parseGoals } from '../../lib/carePlan'
import RequestCallModal from '../shared/RequestCallModal'
import CarePlanImport from './CarePlanImport'

/** Keyword-matched so it works for any plan, not a fixed goal list. */
const ICON_RULES = [
  { match: ['fall', 'safety', 'balance', 'mobility', 'trip'], icon: '🦺' },
  { match: ['medication', 'dose', 'pill', 'prescription'], icon: '💊' },
  { match: ['social', 'lonely', 'isolat', 'friend', 'visit'], icon: '🤝' },
  { match: ['sleep', 'night', 'rest'], icon: '😴' },
  { match: ['memory', 'cognit', 'confus', 'dementia'], icon: '🧠' },
  { match: ['mood', 'depress', 'anxious', 'wellbeing'], icon: '❤️' },
]

function goalIcon(goal) {
  const hay = `${goal.title} ${goal.description}`.toLowerCase()
  for (const r of ICON_RULES) if (r.match.some((m) => hay.includes(m))) return r.icon
  return '🎯'
}

/**
 * Status is derived, not stored:
 *   no actions yet          → New
 *   every action done       → Complete
 *   high-priority goal      → Review
 *   otherwise               → Ongoing
 */
function goalStatus(goal, actions) {
  if (actions.length === 0) return { label: 'New', className: 'badge-blue' }
  if (actions.every((a) => a.is_completed)) return { label: 'Complete', className: 'badge-grey' }
  if (goal.priority === 'high') return { label: 'Review', className: 'badge-amber' }
  return { label: 'Ongoing', className: 'badge-green' }
}

const initialOf = (name) => (name || '?').trim().charAt(0).toUpperCase()

export default function CarePlanActionsList() {
  const { user } = useAuth()
  const { recipient, role } = useCare()
  const toast = useToast()

  const [loading, setLoading] = useState(true)
  const [plan, setPlan] = useState(null)
  const [actions, setActions] = useState([])
  const [team, setTeam] = useState([])
  const [expanded, setExpanded] = useState({})
  const [showDone, setShowDone] = useState({})
  const [addFor, setAddFor] = useState(null) // goal object
  const [addGoalOpen, setAddGoalOpen] = useState(false)
  const [callModal, setCallModal] = useState(null)
  const [importOpen, setImportOpen] = useState(false)
  const [busyId, setBusyId] = useState(null)

  const canEdit = role === 'admin' || role === 'family_member'
  const isAdmin = role === 'admin'

  const load = useCallback(async () => {
    if (!recipient) return
    const [planRes, actionRes, teamRes] = await Promise.all([
      supabase
        .from('care_plans').select('*')
        .eq('care_recipient_id', recipient.id).eq('is_active', true)
        .order('created_at', { ascending: false }).limit(1).maybeSingle(),
      supabase
        .from('care_plan_actions').select('*')
        .eq('care_recipient_id', recipient.id)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true }),
      supabase
        .from('care_team_members')
        .select('id, user_id, invited_name, relationship, profile:profiles(id, full_name)')
        .eq('care_recipient_id', recipient.id).eq('is_active', true),
    ])
    setPlan(planRes.data || null)
    setActions(actionRes.data || [])
    setTeam(teamRes.data || [])
    setLoading(false)
  }, [recipient])

  useEffect(() => { load() }, [load])

  if (!recipient || loading) return <LoadingSpinner label="Loading care plan…" />

  const goals = parseGoals(plan?.current_priorities)
  // Actions are keyed to the goal title (see the migration comment).
  const forGoal = (goal) => actions.filter((a) => a.goal_reference === goal.title)

  const totalActions = actions.length
  const doneActions = actions.filter((a) => a.is_completed).length

  const memberName = (profileId) => {
    const m = team.find((t) => t.profile?.id === profileId || t.user_id === profileId)
    return m?.profile?.full_name || m?.invited_name || null
  }

  async function toggleAction(action) {
    if (!canEdit || busyId) return
    const next = !action.is_completed
    setBusyId(action.id)
    // Optimistic: the circle should respond on tap, not after a round trip.
    setActions((list) => list.map((a) => (a.id === action.id ? { ...a, is_completed: next } : a)))
    const { error } = await supabase
      .from('care_plan_actions')
      .update({
        is_completed: next,
        completed_at: next ? new Date().toISOString() : null,
        completed_by: next ? user.id : null,
      })
      .eq('id', action.id)
    setBusyId(null)
    if (error) {
      setActions((list) => list.map((a) => (a.id === action.id ? { ...a, is_completed: !next } : a)))
      toast.error(humanizeError(error))
    }
  }

  return (
    <div className="stack">
      {isAdmin && (
        <button className="btn btn-secondary btn-block" onClick={() => setImportOpen(true)}>
          📋 Import existing care plan
        </button>
      )}

      {/* Summary bar */}
      <div
        className="card"
        style={{ background: 'var(--teal)', borderColor: 'transparent', color: '#fff' }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          <Stat label="Goals" value={goals.length} />
          <Stat label="Actions" value={totalActions} />
          <Stat label="Completed" value={doneActions} />
        </div>
      </div>

      {goals.length === 0 ? (
        <EmptyState
          icon="🎯"
          title="No care plan yet"
          message="Once there are goals in the care plan, you can add the specific actions for each one here."
          action={
            canEdit && (
              <button className="btn" onClick={() => setAddGoalOpen(true)}>+ Add a goal</button>
            )
          }
        />
      ) : (
        <div className="stack" style={{ gap: 10 }}>
          {goals.map((goal) => {
            const mine = forGoal(goal)
            const done = mine.filter((a) => a.is_completed)
            const open = mine.filter((a) => !a.is_completed)
            const pct = mine.length ? Math.round((done.length / mine.length) * 100) : 0
            const status = goalStatus(goal, mine)
            const isOpen = !!expanded[goal.id]

            return (
              <div key={goal.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <button
                  onClick={() => setExpanded((e) => ({ ...e, [goal.id]: !e[goal.id] }))}
                  aria-expanded={isOpen}
                  style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: 16 }}
                >
                  <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 22, lineHeight: 1.2 }} aria-hidden>{goalIcon(goal)}</span>
                    <div className="grow" style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, lineHeight: 1.4 }}>{goal.title}</div>
                      <div className="row wrap" style={{ gap: 6, marginTop: 6 }}>
                        <span className={`badge ${status.className}`}>{status.label}</span>
                        <span className="muted" style={{ fontSize: 13 }}>
                          {mine.length === 0
                            ? 'No actions yet'
                            : `${done.length} of ${mine.length} done`}
                        </span>
                      </div>
                    </div>
                    <span className="muted" aria-hidden style={{ flexShrink: 0 }}>{isOpen ? '▾' : '▸'}</span>
                  </div>
                </button>

                {isOpen && (
                  <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--border)' }}>
                    {goal.description && (
                      <p className="muted" style={{ fontSize: 14, lineHeight: 1.55, marginTop: 12 }}>
                        {goal.description}
                      </p>
                    )}

                    {mine.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div className="meter"><span style={{ width: `${pct}%`, background: 'var(--green)' }} /></div>
                        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                          {done.length} of {mine.length} actions complete · {pct}%
                        </div>
                      </div>
                    )}

                    <div className="stack" style={{ gap: 10, marginTop: 12 }}>
                      {open.map((a) => (
                        <ActionRow
                          key={a.id} action={a} canEdit={canEdit}
                          assignee={memberName(a.assigned_to)}
                          onToggle={() => toggleAction(a)}
                        />
                      ))}

                      {done.length > 0 && (
                        <>
                          <button
                            onClick={() => setShowDone((s) => ({ ...s, [goal.id]: !s[goal.id] }))}
                            style={{ background: 'none', border: 'none', color: 'var(--teal)', fontWeight: 600, fontSize: 14, padding: 0, textAlign: 'left' }}
                          >
                            {done.length} completed · {showDone[goal.id] ? 'Hide' : 'Show'}
                          </button>
                          {showDone[goal.id] && done.map((a) => (
                            <ActionRow
                              key={a.id} action={a} canEdit={canEdit}
                              assignee={memberName(a.assigned_to)}
                              onToggle={() => toggleAction(a)}
                            />
                          ))}
                        </>
                      )}

                      {mine.length === 0 && (
                        <p className="muted" style={{ fontSize: 14, margin: 0 }}>
                          Nothing planned for this goal yet.
                        </p>
                      )}

                      {canEdit && (
                        <button className="btn btn-ghost btn-block" onClick={() => setAddFor(goal)}>
                          + Add action
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })}

          {canEdit && (
            <button
              onClick={() => setAddGoalOpen(true)}
              style={{
                border: '1.5px dashed var(--border)', borderRadius: 'var(--radius)',
                background: 'none', padding: 18, color: 'var(--teal)', fontWeight: 600,
                fontSize: 15, cursor: 'pointer', width: '100%',
              }}
            >
              + Add a new goal
            </button>
          )}
        </div>
      )}

      {/* Upgrade CTA — sits below the goal actions on both the tab and the
          standalone page, since it is about the plan itself. */}
      <div
        className="card stack"
        style={{ gap: 10, borderColor: 'var(--pa)', borderWidth: 1.5, background: 'var(--pa-light)' }}
      >
        <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 20, lineHeight: 1.2 }} aria-hidden>📊</span>
          <div style={{ fontWeight: 700, lineHeight: 1.4 }}>
            Care plans followed with support have 3× better adherence
          </div>
        </div>
        <p className="muted" style={{ fontSize: 14, lineHeight: 1.55, margin: 0 }}>
          Families who work with a MaPa nurse and coordinator are significantly
          more likely to achieve their care goals — and catch problems before
          they become crises.
        </p>
        <p className="muted" style={{ fontSize: 14, lineHeight: 1.55, margin: 0 }}>
          This is remote care — no home visits. A nurse on video, a coordinator
          by phone.
        </p>
        <div className="row wrap" style={{ gap: 8 }}>
          <button className="btn btn-sm grow" onClick={() => setCallModal('plus')}>
            Have our nurse build this plan
          </button>
          <button className="btn btn-sm btn-secondary grow" onClick={() => setCallModal('connect')}>
            Get a coordinator
          </button>
        </div>
      </div>

      {importOpen && (
        <CarePlanImport
          recipientId={recipient.id}
          plan={plan}
          onCancel={() => setImportOpen(false)}
          onDone={() => { setImportOpen(false); load() }}
        />
      )}

      {callModal && (
        <RequestCallModal plan={callModal} onClose={() => setCallModal(null)} />
      )}

      {addFor && (
        <AddActionSheet
          goal={addFor}
          recipientId={recipient.id}
          planId={plan?.id || null}
          team={team}
          userId={user.id}
          nextSort={actions.length}
          onCancel={() => setAddFor(null)}
          onDone={() => { setAddFor(null); load() }}
        />
      )}

      {addGoalOpen && (
        <AddGoalSheet
          plan={plan}
          recipientId={recipient.id}
          onCancel={() => setAddGoalOpen(false)}
          onDone={() => { setAddGoalOpen(false); load() }}
        />
      )}

      <div style={{ height: 8 }} />
    </div>
  )
}

function Stat({ label, value }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.2 }}>{value}</div>
      <div style={{ fontSize: 12, opacity: 0.85 }}>{label}</div>
    </div>
  )
}

function ActionRow({ action, canEdit, assignee, onToggle }) {
  const done = action.is_completed
  return (
    <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
      <button
        type="button"
        onClick={onToggle}
        disabled={!canEdit}
        aria-pressed={done}
        aria-label={done ? `Mark "${action.title}" not done` : `Mark "${action.title}" done`}
        style={{
          width: 24, height: 24, borderRadius: '50%', flexShrink: 0, marginTop: 2,
          border: done ? '2px solid var(--green)' : '2px solid var(--border)',
          background: done ? 'var(--green)' : 'transparent',
          color: '#fff', fontSize: 13, lineHeight: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: canEdit ? 'pointer' : 'default', padding: 0,
        }}
      >
        {done ? '✓' : ''}
      </button>
      <div className="grow" style={{ minWidth: 0, opacity: done ? 0.55 : 1 }}>
        <div
          style={{
            fontWeight: 600, lineHeight: 1.4,
            textDecoration: done ? 'line-through' : 'none',
          }}
        >
          {action.title}
        </div>
        {action.description && (
          <div className="muted" style={{ fontSize: 13, marginTop: 2, lineHeight: 1.5 }}>
            {action.description}
          </div>
        )}
      </div>
      {assignee && (
        <span
          title={assignee}
          aria-label={`Assigned to ${assignee}`}
          style={{
            width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
            background: 'var(--pa)', color: '#fff', fontSize: 11, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {initialOf(assignee)}
        </span>
      )}
    </div>
  )
}

function Sheet({ title, onCancel, children }) {
  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onCancel()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
    >
      <div style={{ background: 'var(--bg)', width: '100%', maxWidth: 'var(--maxw)', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20, maxHeight: '92dvh', overflowY: 'auto' }}>
        <div className="spread" style={{ marginBottom: 12 }}>
          <h2 className="title" style={{ fontSize: 20 }}>{title}</h2>
          <button onClick={onCancel} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--muted)' }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  )
}

function AddActionSheet({ goal, recipientId, planId, team, userId, nextSort, onCancel, onDone }) {
  const toast = useToast()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [assignedTo, setAssignedTo] = useState('')
  const [busy, setBusy] = useState(false)

  // Only members with a real profile can be assigned — assigned_to is a
  // profiles FK, so invite-only members have nothing to point at yet.
  const assignable = team.filter((m) => m.profile?.id)

  async function save() {
    if (!title.trim()) return toast.error('Give the action a title.')
    setBusy(true)
    const { error } = await supabase.from('care_plan_actions').insert({
      care_recipient_id: recipientId,
      care_plan_id: planId,
      goal_reference: goal.title,
      title: title.trim(),
      description: description.trim() || null,
      assigned_to: assignedTo || null,
      sort_order: nextSort,
      created_by: userId,
    })
    setBusy(false)
    if (error) return toast.error(humanizeError(error))
    toast.success('Action added.')
    onDone()
  }

  return (
    <Sheet title="Add action" onCancel={onCancel}>
      <div className="stack">
        <p className="muted" style={{ fontSize: 14, margin: 0, lineHeight: 1.5 }}>
          For goal: <strong>{goal.title}</strong>
        </p>
        <div className="field">
          <label className="label" htmlFor="a-title">Action</label>
          <input
            id="a-title" className="input" value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Install grab bars in the bathroom"
          />
        </div>
        <div className="field">
          <label className="label" htmlFor="a-desc">
            Description <span className="hint">— optional</span>
          </label>
          <textarea
            id="a-desc" className="textarea" value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Any detail worth remembering"
          />
        </div>
        <div className="field">
          <label className="label" htmlFor="a-assign">
            Assign to <span className="hint">— optional</span>
          </label>
          <select
            id="a-assign" className="select" value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
          >
            <option value="">Nobody in particular</option>
            {assignable.map((m) => (
              <option key={m.id} value={m.profile.id}>{m.profile.full_name}</option>
            ))}
          </select>
        </div>
        <button className="btn btn-block btn-lg" onClick={save} disabled={busy}>
          {busy ? <span className="spinner" /> : 'Save action'}
        </button>
      </div>
    </Sheet>
  )
}

function AddGoalSheet({ plan, recipientId, onCancel, onDone }) {
  const toast = useToast()
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)

  async function save() {
    const line = text.trim()
    if (!line) return toast.error('Describe the goal.')
    setBusy(true)
    // Goals live as bullet lines in care_plans.current_priorities, so a new
    // goal is appended in the same format the parser already reads.
    const existing = plan?.current_priorities ? `${plan.current_priorities}\n` : ''
    const next = `${existing}• ${line}`
    const res = plan
      ? await supabase.from('care_plans').update({ current_priorities: next }).eq('id', plan.id)
      : await supabase.from('care_plans').insert({
          care_recipient_id: recipientId,
          current_priorities: next,
          is_active: true,
        })
    setBusy(false)
    if (res.error) return toast.error(humanizeError(res.error))
    toast.success('Goal added.')
    onDone()
  }

  return (
    <Sheet title="Add a goal" onCancel={onCancel}>
      <div className="stack">
        <div className="field">
          <label className="label" htmlFor="g-text">Goal</label>
          <textarea
            id="g-text" className="textarea" value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. Prevent falls — she has had two near-misses in the bathroom"
          />
          <span className="hint">
            Tip: use an em dash to separate the goal from the detail.
          </span>
        </div>
        <button className="btn btn-block btn-lg" onClick={save} disabled={busy}>
          {busy ? <span className="spinner" /> : 'Save goal'}
        </button>
      </div>
    </Sheet>
  )
}
